import { gql } from '@apollo/client'
import { StoreModel } from './index'
import { getFormNameForQuestionSet } from './tracking'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import { graphQL } from '@mortgage-pos/ui/services/http'
import incense from '@mortgage-pos/ui/incense'
import { rateCodesMap } from '@mortgage-pos/data'
import { action, Action, Computed, computed, thunk, Thunk } from 'easy-peasy'
import { alfieTrackingService } from '@mortgage-pos/shared-frontend'
import {
  hasAxiosErrors,
  getGraphQLString,
  LoanCostsCalculator,
  deriveLoanTermInMonths,
  extractFeeByDescription,
  calculatePurchaseLoanToValue,
  calculateRefinanceLoanToValue,
  calculateEffectiveWaiveEscrow,
  STATE_MINIMUM_LOCK_PERIOD,
} from '@mortgage-pos/utils'

import {
  ALFIE_EXPERIENCE,
  Rate,
  RateInput,
  LoanPurpose,
  RatesRequest,
  RatesRequestSource,
  LockRequestType,
  QuotingChannel,
  ProductTerm,
} from '@mortgage-pos/types'

const PRICING_REQUEST_FAILED_ERROR_DETAILS = 'Pricing request failed'

export interface RequestRatesPayload {
  debtToIncome?: number
  loanTerm?: ProductTerm[]
}

export interface RatesModel {
  rates: Rate[]
  selectedRate: Rate
  rateSelectionPersisted: boolean
  isRequestingRates: boolean
  debtToIncome: number
  setDebtToIncome: Action<RatesModel, number>
  loanToValue: Computed<RatesModel, number, StoreModel>
  effectiveWaiveEscrow: Computed<RatesModel, boolean, StoreModel>
  sortedRates: Computed<RatesModel, Rate[], StoreModel>
  setRates: Action<RatesModel, Rate[]>
  setSelectedRate: Action<RatesModel, Rate>
  setRateSelectionPersisted: Action<RatesModel, boolean>
  requestRates: Thunk<
    RatesModel,
    RequestRatesPayload | number | undefined,
    object,
    StoreModel
  >
  requestTermMatchRate: Thunk<
    RatesModel,
    ProductTerm,
    object,
    StoreModel,
    Promise<Rate[]>
  >
  setIsRequestingRates: Action<RatesModel, boolean>
  submitRateSelection: Thunk<RatesModel, Rate, object, StoreModel>
  submitLockForm: Thunk<RatesModel, Rate, object, StoreModel>
  lockWipLockForm: Thunk<RatesModel, null, object, StoreModel>
}

const rates = (): RatesModel => {
  return {
    rates: [],

    selectedRate: null,

    rateSelectionPersisted: false,

    isRequestingRates: false,

    debtToIncome: null,

    setDebtToIncome: action((state, payload) => {
      state.debtToIncome = payload
    }),

    setRateSelectionPersisted: action((state, payload) => {
      state.rateSelectionPersisted = payload
    }),

    loanToValue: computed(
      [
        (_, storeState) => storeState.answers.mergedAnswers,
        (_, storeState) => storeState.propertyInfo.avmEstimatedValue,
        (_, storeState) => storeState.rates.rates,
      ],
      (mergedAnswers, avmEstimatedValue, storedRates) => {
        if (mergedAnswers.loanPurpose === LoanPurpose.Purchase) {
          const { propertyPurchasePrice, propertyDownPayment } = mergedAnswers
          const hasPurchaseFields =
            !isNaN(propertyPurchasePrice) && !isNaN(propertyDownPayment)

          if (!hasPurchaseFields) {
            return 100
          }

          return calculatePurchaseLoanToValue(
            propertyPurchasePrice,
            propertyDownPayment
          )
        }

        const estimatedValue = mergedAnswers.estimatedValue || avmEstimatedValue

        if (!estimatedValue) {
          return 100
        }

        const { balanceWithFees } = getLoanAmounts(mergedAnswers, storedRates)

        return calculateRefinanceLoanToValue(
          balanceWithFees,
          estimatedValue,
          mergedAnswers.cashOut
        )
      }
    ),

    effectiveWaiveEscrow: computed(
      [
        (_, storeState) => storeState.answers.mergedAnswers.loanPurpose,
        (_, storeState) => storeState.rates.loanToValue,
      ],
      (loanPurpose, ltv) => calculateEffectiveWaiveEscrow(loanPurpose, ltv)
    ),

    sortedRates: computed((state) => {
      // per biz, only targeting 30, 20, 15 year fixed rates
      const filteredRates = state.rates.filter(
        (rate) => rate.type === 'Fixed' && rate.term !== 'F40'
      )

      const sortWeights = {
        F30: 1,
        F25: 2,
        F20: 3,
        F15: 4,
        F10: 5,
        F40: 6,
        A1_1: 7,
        A3_1: 8,
        A5_1: 9,
        A2_1: 10,
        A5_5: 11,
        A7_1: 12,
        A10_1: 13,
        A15_1: 14,
      }

      return filteredRates.sort((a, b) => {
        return sortWeights[a.term] - sortWeights[b.term]
      })
    }),

    setRates: action((state, payload) => {
      state.rates = payload
    }),

    setSelectedRate: action((state, payload) => {
      state.selectedRate = payload
    }),

    setIsRequestingRates: action((state, payload) => {
      state.isRequestingRates = payload
    }),

    requestRates: thunk(
      async (
        { setIsRequestingRates, setRates },
        payload: RequestRatesPayload | number | undefined = {},
        { getState, getStoreState, getStoreActions }
      ) => {
        const storeActions = getStoreActions()
        const {
          answers: { setAnswers },
          application: { updateStatus },
          tracking,
        } = storeActions
        const debtToIncome =
          typeof payload === 'number' ? payload : payload?.debtToIncome
        const loanTerm =
          typeof payload === 'object' ? payload?.loanTerm : undefined
        const { loanToValue, rates: storedRates } = getState()
        const { mergedAnswers } = getStoreState().answers
        applyEffectiveEscrowAnswer(mergedAnswers, loanToValue, setAnswers)
        const refreshedAnswers = getStoreState().answers.mergedAnswers
        const { loanToValue: refreshedLoanToValue } = getState()
        const { applicationId } = getStoreState().application
        const {
          statusesMap,
          isRobinhoodGoldFlow,
          isRobinhoodGoldMember,
          isSimpleV2Flow,
          pageIndex,
          currentPageName,
          slug,
        } = getStoreState().questionnaire
        const { avmEstimatedValue } = getStoreState().propertyInfo

        const { hasRunCreditPull, isLeComparisonApp, wasCreditPullSuccessful } =
          getStoreState().leComparison

        setIsRequestingRates(true)

        // If user provided a credit score estimate, we can init w/ that value
        let creditScoreOverride: number = isSimpleV2Flow
          ? undefined
          : (Number(mergedAnswers.creditScore) ?? null)
        const fallbackCreditScore = isSimpleV2Flow
          ? getSimpleV2FallbackCreditScore(mergedAnswers)
          : undefined

        // Use fallback credit score in compare flow if we couldn't pull credit
        if (isLeComparisonApp && hasRunCreditPull && !wasCreditPullSuccessful) {
          creditScoreOverride = creditScoreOverride ?? 740
        }

        let resp

        console.log('Calling getRates')

        try {
          resp = await getRates(
            refreshedAnswers,
            refreshedLoanToValue,
            applicationId,
            avmEstimatedValue,
            storedRates,
            debtToIncome,
            creditScoreOverride,
            isRobinhoodGoldFlow,
            isRobinhoodGoldMember,
            loanTerm,
            fallbackCreditScore
          )
        } catch (error) {
          await updateStatus(statusesMap.noRates)
          newRelic.increment('rates_request.error')
          setIsRequestingRates(false)
          if (isSimpleV2Flow) {
            tracking.formErrored({
              errorType: 'pricing_request_failed',
              errorMessage: PRICING_REQUEST_FAILED_ERROR_DETAILS,
              errorDetails: PRICING_REQUEST_FAILED_ERROR_DETAILS,
            })
          }

          incense(error)
            .details({
              name: 'RequestRatesFailure',
              message: 'Failed to fetch rates',
            })
            .safe({
              applicationId,
              loanToValue: refreshedLoanToValue,
              debtToIncome,
              loanTerm,
            })
            .sensitive({
              mergedAnswers: refreshedAnswers,
              avmEstimatedValue,
              creditScoreOverride,
            })
            .error()
            .rethrow()
        }

        if (resp.data.errors && resp.data.errors.length) {
          await updateStatus(statusesMap.noRates)

          newRelic.increment('rates_request.error')
          setIsRequestingRates(false)
          if (isSimpleV2Flow) {
            tracking.formErrored({
              errorType: 'pricing_request_failed',
              errorMessage: PRICING_REQUEST_FAILED_ERROR_DETAILS,
              errorDetails: PRICING_REQUEST_FAILED_ERROR_DETAILS,
            })
          }

          incense(resp.data.errors)
            .details({
              name: 'RequestRatesFailure',
              message: 'Failed to fetch rates',
            })
            .safe({
              applicationId,
              loanToValue: refreshedLoanToValue,
              debtToIncome,
              loanTerm,
            })
            .sensitive({
              mergedAnswers: refreshedAnswers,
              avmEstimatedValue,
              creditScoreOverride,
            })
            .error()
            .rethrow()
        }

        const rates = resp.data.data.getRates

        if (rates.length) {
          await updateStatus(statusesMap.rates)
          newRelic.increment('rates_request.success', [
            { key: 'count', value: rates.length.toString() },
          ])
        } else {
          await updateStatus(statusesMap.noRates)
          newRelic.increment('rates_request.no_rates')
        }

        setRates(rates)
        setIsRequestingRates(false)
        if (isSimpleV2Flow) {
          tracking.elementLoaded({
            elementName: 'pricing_request_completed',
            location: 'simple_v2_lifecycle',
          })
          if (rates.length) {
            alfieTrackingService.alfieRatesDisplayed({
              experience: ALFIE_EXPERIENCE.simpleV2,
              formId: getStoreState().tracking.formId,
              formName: getFormNameForQuestionSet(slug),
              pageName: currentPageName,
              stepNumber: pageIndex,
            })
          }
        }
        return rates
      }
    ),

    requestTermMatchRate: thunk(
      async (
        _,
        productTerm: ProductTerm,
        { getState, getStoreState, getStoreActions }
      ) => {
        const { loanToValue, rates: storedRates } = getState()
        const { mergedAnswers } = getStoreState().answers
        applyEffectiveEscrowAnswer(
          mergedAnswers,
          loanToValue,
          getStoreActions().answers.setAnswers
        )
        const refreshedAnswers = getStoreState().answers.mergedAnswers
        const { loanToValue: refreshedLoanToValue } = getState()
        const { applicationId } = getStoreState().application
        const { isRobinhoodGoldFlow, isRobinhoodGoldMember, isSimpleV2Flow } =
          getStoreState().questionnaire
        const { avmEstimatedValue } = getStoreState().propertyInfo

        const { hasRunCreditPull, isLeComparisonApp, wasCreditPullSuccessful } =
          getStoreState().leComparison

        let creditScoreOverride: number = isSimpleV2Flow
          ? undefined
          : (Number(mergedAnswers.creditScore) ?? null)
        const fallbackCreditScore = isSimpleV2Flow
          ? getSimpleV2FallbackCreditScore(mergedAnswers)
          : undefined

        if (isLeComparisonApp && hasRunCreditPull && !wasCreditPullSuccessful) {
          creditScoreOverride = creditScoreOverride ?? 740
        }

        try {
          const resp = await getRates(
            refreshedAnswers,
            refreshedLoanToValue,
            applicationId,
            avmEstimatedValue,
            storedRates,
            undefined, // No debtToIncome
            creditScoreOverride,
            isRobinhoodGoldFlow,
            isRobinhoodGoldMember,
            [productTerm], // Specific term only
            fallbackCreditScore
          )

          const rates = resp.data.data.getRates
          return rates
        } catch (error) {
          incense(error)
            .details({
              name: 'RequestTermMatchRateFailure',
              message: 'Failed to fetch term-matched rate',
            })
            .safe({ applicationId, productTerm })
            .sensitive({ mergedAnswers: refreshedAnswers })
            .error()

          throw error
        }
      }
    ),

    submitRateSelection: thunk(
      async (
        { setSelectedRate, setRateSelectionPersisted },
        rate: Rate,
        { getStoreState }
      ) => {
        setSelectedRate(rate)
        setRateSelectionPersisted(false)

        const { isSimpleV2Flow, pageIndex, currentPageName, slug } =
          getStoreState().questionnaire

        let response
        let rateInput: RateInput

        try {
          rateInput = rateToRateInput(rate)

          response = await graphQL({
            query: submitRateSelectionQuery,
            variables: { applicationId: null, rate: rateInput, isSimpleV2Flow },
          })
        } catch (error) {
          newRelic.increment('q_apply.rates_submission.error')

          incense(error)
            .details({
              name: 'SubmitRateSelectionFailure',
              message: 'Failed to fetch rates',
            })
            .sensitive({ rate, rateInput })
            .error()

          return
        }

        if (hasAxiosErrors(response)) {
          newRelic.increment('q_apply.rates_submission.error')

          incense(response.data.errors)
            .details({
              name: 'SubmitRateSelectionFailure',
              message: 'Request errors occurred during execution',
            })
            .sensitive({ rate, rateInput })
            .error()

          return
        }

        // The resolver reports insert failures as `false`, not GraphQL errors.
        const persisted = response.data.data.submitRateSelection === true
        setRateSelectionPersisted(persisted)

        if (isSimpleV2Flow && persisted) {
          alfieTrackingService.alfieRateSelected({
            experience: ALFIE_EXPERIENCE.simpleV2,
            formId: getStoreState().tracking.formId,
            formName: getFormNameForQuestionSet(slug),
            pageName: currentPageName,
            stepNumber: pageIndex,
          })
        }
        newRelic.increment('q_apply.rates_submission.success')
      }
    ),

    submitLockForm: thunk(
      async (_, rate: Rate, { getStoreState, getState, getStoreActions }) => {
        const { rates: storedRates } = getState()
        const { debtToIncome } = getStoreState().rates
        const { applicationId } = getStoreState().application
        const { mergedAnswers } = getStoreState().answers
        const { isPrePricingFlow } = getStoreState().questionnaire
        const { remainingBalance } = getLoanAmounts(mergedAnswers, storedRates)
        const { setSelectedRate } = getStoreActions().rates

        let response
        let gqlVars: Record<string, any> = {}

        setSelectedRate(rate)

        const handleErrors = async (error, step = '', message = '') => {
          newRelic.increment('ntl.submit_lock_form.error', [
            { key: 'failureStep', value: step },
          ])

          await graphQL({
            query: triggerLockDeskFailureAlert,
            variables: { applicationId: null },
          })

          incense(error)
            .details({
              name: 'SubmitLockFormError',
              message,
            })
            .safe({ applicationId })
            .sensitive({ gqlVars })
            .error()
        }

        try {
          gqlVars = {
            applicationId: null,
            rate: rateToRateInput(rate),
            pricingScenarioInfo: {
              remainingBalance: Math.round(remainingBalance), // GQL expects int
              debtToIncome,
            },
            lockRequestType: isPrePricingFlow
              ? LockRequestType.Wip
              : LockRequestType.Lock,
          }

          response = await graphQL({
            query: submitLockFormQuery,
            variables: gqlVars,
          })
        } catch (error) {
          await handleErrors(
            error,
            'GQL Response',
            'Failed on lock form GQL request'
          )
          return
        }

        if (hasAxiosErrors(response)) {
          await handleErrors(
            response.data.errors,
            'Axios error',
            'Failed on lock form HTTP request'
          )
          return
        }

        newRelic.increment('ntl.submit_lock_form.success')
      }
    ),

    lockWipLockForm: thunk(async (_, __, { getStoreState, getState }) => {
      const { applicationId } = getStoreState().application

      let response

      const handleErrors = async (error, step = '') => {
        newRelic.increment('ntl.lock_wip_lock_form.error', [
          { key: 'failureStep', value: step },
        ])

        await graphQL({
          query: triggerLockDeskFailureAlert,
          variables: { applicationId: null },
        })

        incense(error)
          .details({
            name: 'LockWipLockFormError',
            message: 'Failed on lock form GQL request',
          })
          .safe({ applicationId })
          .error()
      }

      try {
        response = await graphQL({
          query: lockWipLockFormQuery,
        })
      } catch (error) {
        await handleErrors(error, 'GQL Response')
        return
      }

      if (hasAxiosErrors(response)) {
        await handleErrors(response.data.errors, 'Axios error')
        return
      }

      newRelic.increment('ntl.lock_wip_lock_form.success')
    }),
  }
}

export default rates

export function getSimpleV2FallbackCreditScore(answers: {
  creditScore?: number | string | null
  coBorrowerCreditScore?: number | string | null
}): number | undefined {
  const coBorrowerCreditScore = Number(answers.coBorrowerCreditScore)
  if (!Number.isFinite(coBorrowerCreditScore) || coBorrowerCreditScore <= 0) {
    return undefined
  }

  const selfReportedScores = [
    answers.creditScore,
    answers.coBorrowerCreditScore,
  ]
    .map(Number)
    .filter((score) => Number.isFinite(score) && score > 0)

  return Math.min(...selfReportedScores)
}

function applyEffectiveEscrowAnswer(
  answers: { escrow?: string | null; loanPurpose?: LoanPurpose | null },
  loanToValue: number,
  setAnswers: (payload: { escrow: 'Yes' | 'No' }) => void
): void {
  // Escrow waiver is refi-only. Don't write fresh values on non-refi, but
  // do clear a stale 'Yes' left over from an earlier refi stamp in the same
  // session (e.g. borrower restarted via /welcome with a different purpose).
  if (answers.loanPurpose !== LoanPurpose.Refinance) {
    if (answers.escrow === 'Yes') {
      setAnswers({ escrow: 'No' })
    }
    return
  }

  const effectiveEscrowAnswer = calculateEffectiveWaiveEscrow(
    answers.loanPurpose,
    loanToValue
  )
    ? 'Yes'
    : 'No'
  if (answers.escrow !== effectiveEscrowAnswer) {
    setAnswers({ escrow: effectiveEscrowAnswer })
  }
}

async function getRates(
  answers,
  loanToValue,
  applicationId,
  avmEstimatedValue,
  storedRates,
  debtToIncome,
  creditScoreOverride?: number,
  isRobinhoodGoldFlow?: boolean,
  isRobinhoodGoldMember?: boolean,
  loanTerm?: ProductTerm[], // Array of ProductTerm values (e.g., [ProductTerm.F12])
  fallbackCreditScore?: number
) {
  const {
    remainingBalance,
    isRollInEscrow,
    escrowFees,
    isRollInFees,
    rollInFees,
  } = getLoanAmounts(answers, storedRates)

  const request: RatesRequest = {
    loanToValue,
    loanPurpose: answers.loanPurpose ?? LoanPurpose.Refinance,
    propertyType: answers.propertyType,
    propertyUse: answers.propertyUse,
    zipCode: answers.zipCode,
    address: answers.street,
    state: answers.state,
    city: answers.city,
    rollInFees: isRollInFees ? rollInFees : 0,
    rollInEscrow: isRollInEscrow ? escrowFees : 0,
    willRollInFees: isRollInFees,
    willRollInEscrow: isRollInEscrow,
    waiveEscrow: answers.escrow === 'Yes',
    source: RatesRequestSource.Full1003,
    selfEmployed: checkForSelfEmployedBorrower(answers),
    applicationId: applicationId,
  }

  // Override lock period for states that mandate a higher minimum (e.g. OK
  // requires 45 days). Without this MQ stops every quote and the borrower
  // sees no rates. Scoped to the Alfie request so the global pricing default
  // is unaffected.
  const stateMinimumLockPeriod = STATE_MINIMUM_LOCK_PERIOD[answers.state]
  if (stateMinimumLockPeriod) {
    request.lockPeriod = stateMinimumLockPeriod
  }

  if (creditScoreOverride) {
    request.creditScore = creditScoreOverride
  }

  if (fallbackCreditScore) {
    request.fallbackCreditScore = fallbackCreditScore
  }

  if (answers.loanPurpose === LoanPurpose.Purchase) {
    request.propertyPurchasePrice = answers.propertyPurchasePrice
    request.propertyDownPayment = answers.propertyDownPayment
    // Pricing ignores propertyValue on purchases; it is echoed onto the rate, and
    // VEPA and DU read it from prelocked_rates as the purchase price (matches Ello)
    request.propertyValue = answers.propertyPurchasePrice
  } else {
    request.propertyValue = answers.estimatedValue || avmEstimatedValue
    request.remainingBalance = remainingBalance
    request.cashOut = answers.cashOut || null
  }

  if (debtToIncome) {
    request.debtToIncome = Math.floor(debtToIncome)
  }

  if (isRobinhoodGoldFlow && isRobinhoodGoldMember) {
    request.quotingChannel = QuotingChannel.Rh
  }

  if (loanTerm && loanTerm.length > 0) {
    request.loanTerm = loanTerm
  }

  console.log('Request:', request)

  return graphQL({
    query: ratesQuery,
    variables: { request },
  })
}

export function checkForSelfEmployedBorrower(answers) {
  const primaryIncome = answers.incomeSources || []
  const coBorrowerIncome = answers.coBorrowerIncomeSources || []
  const incomeSources = [...primaryIncome, ...coBorrowerIncome]

  return (
    incomeSources.length > 0 &&
    incomeSources.some((source) => source.incomeType === 'selfEmployed')
  )
}

export interface LoanAmounts {
  remainingBalance: number
  isRollInEscrow: boolean
  escrowFees: number
  isRollInFees: boolean
  rollInFees: number
  balanceWithFees: number
}

export function getLoanAmounts(answers, storedRates): LoanAmounts {
  const {
    cashOut,
    rollInFees: willRollInFees,
    escrowRollIn,
    remainingBalance,
    escrow: waiveEscrow,
    previouslySelectedRate,
  } = answers

  let escrowFeesAmount = 0
  let rollInFeesAmount = 0
  let matchedRate: Rate = {}

  if (storedRates.length) {
    const bankrateSelectedRate = storedRates.find(({ rate, term }) => {
      const termName = rateCodesMap[term]
      const termInMonths = deriveLoanTermInMonths(termName)

      return (
        rate === previouslySelectedRate?.rate &&
        termInMonths === previouslySelectedRate?.termInMonths
      )
    })

    if (bankrateSelectedRate) {
      matchedRate = bankrateSelectedRate
    } else {
      const termSelected = previouslySelectedRate?.termInMonths || 360

      const lowestRate = storedRates.find(({ term }) => {
        const termName = rateCodesMap[term]
        const termInMonths = deriveLoanTermInMonths(termName)

        return termInMonths === termSelected
      })
      matchedRate = lowestRate
    }

    if (matchedRate?.loanEstimateSections) {
      const loanCostsCalculator = new LoanCostsCalculator(
        {
          cashOut,
          totalFees: matchedRate.loanEstimateSections?.['D']?.total,
          totalEscrow: matchedRate.loanEstimateSections?.['G']?.total,
          lenderCredit: extractFeeByDescription(
            matchedRate.loanEstimateSections?.['J']?.fees,
            'Lender Credit'
          ),
          originationFee: extractFeeByDescription(
            matchedRate.loanEstimateSections?.['A']?.fees,
            'Loan origination fee'
          ),
          taxAndPrepaids:
            matchedRate.loanEstimateSections?.['E']?.total +
            matchedRate.loanEstimateSections?.['F']?.total,
          remainingBalance,
        },
        {
          waiveEscrow: waiveEscrow === 'Yes',
          escrowRollIn: escrowRollIn === 'Yes',
          willRollInFees: willRollInFees === 'Yes',
        }
      )
      const loanCosts = loanCostsCalculator.getLoanCosts()

      escrowFeesAmount =
        (loanCosts.financedCostsDistribution.totalEscrow ?? 0) +
        (loanCosts.financedCostsDistribution.taxAndPrepaids ?? 0)

      rollInFeesAmount = loanCosts.financedCostsDistribution.totalFees ?? 0
    }
  }

  return {
    remainingBalance,
    isRollInEscrow: escrowRollIn === 'Yes',
    isRollInFees: willRollInFees === 'Yes',
    escrowFees: Math.round(escrowFeesAmount),
    rollInFees: Math.round(rollInFeesAmount),
    balanceWithFees: Math.round(
      remainingBalance + escrowFeesAmount + rollInFeesAmount
    ),
  }
}

export const ratesQuery = getGraphQLString(gql`
  fragment loanEstimateSection on Section {
    total
    fees {
      description
      amount
    }
  }

  query getRates($request: RatesRequest) {
    getRates(request: $request) {
      adjustments {
        adjustmentType
        adjustmentSourceType
        isHidden
        isStop
        amount
        title
        isCompAdjustment
        filter
      }
      adjustmentsTotal
      apr
      basePrice
      cashAtClose
      cashOut
      closingCosts
      closingFeeProvider
      combinedLoanToValue
      discountOrCreditAmount
      discountPoints
      discountPointsAsDollarAmount
      fewerUpfrontCosts
      escrowFees
      estimatedPrepaidItems
      family
      fees {
        description
        amount
      }
      finalPrice
      fundingFee
      governmentRefinanceType
      isRollInEscrow
      lenderName
      loanAmount
      loanEstimateSections {
        A {
          ...loanEstimateSection
        }
        B {
          ...loanEstimateSection
        }
        C {
          ...loanEstimateSection
        }
        D {
          ...loanEstimateSection
        }
        E {
          ...loanEstimateSection
        }
        F {
          ...loanEstimateSection
        }
        G {
          ...loanEstimateSection
        }
        H {
          total
        }
        I {
          ...loanEstimateSection
        }
        J {
          ...loanEstimateSection
        }
        Others {
          ...loanEstimateSection
        }
      }
      loanProgramName
      loanPurpose
      loanTerm
      loanToValue
      lockId
      lockPeriod
      monthlyEscrowFees
      monthlyInsurance
      monthlyPayment
      monthlyPrivateMortgageInsurance
      monthlyPropertyTax
      mortgageInsurancePremium {
        annualPercentageRate
        annualDollarPremium
        monthlyPercentageRate
        monthlyDollarPremium
        upfrontPercentageRate
        upfrontDollarPremium
        company
        adjustments
      }
      originationPoints
      points
      principleAndInterestPayment
      privateMortgageInsuranceQuote {
        firstPremiumPeriod {
          premiumAmount
          monthlyRatePercent
          durationInMonths
        }
        secondPremiumPeriod {
          premiumAmount
          monthlyRatePercent
          durationInMonths
        }
      }
      productClass
      productCode
      propertyValue
      quoteType
      rate
      rateSheetPrice
      rollInFees
      silkFees {
        description
        amount
        lineNumber
      }
      subordinateLienAmount
      term
      totalLenderFees
      type
    }
  }
`)

// TODO: remove applicationId from query/mutation
export const submitRateSelectionQuery = getGraphQLString(gql`
  mutation submitRateSelection(
    $applicationId: Int
    $rate: RateInput!
    $isSimpleV2Flow: Boolean
  ) {
    submitRateSelection(
      applicationId: $applicationId
      rate: $rate
      isSimpleV2Flow: $isSimpleV2Flow
    )
  }
`)

// TODO: remove applicationId from query/mutation
export const submitLockFormQuery = getGraphQLString(gql`
  mutation submitLockForm(
    $applicationId: Int
    $rate: RateInput!
    $pricingScenarioInfo: PricingScenarioInfoInput!
    $lockRequestType: LockRequestType
  ) {
    submitLockForm(
      applicationId: $applicationId
      rate: $rate
      pricingScenarioInfo: $pricingScenarioInfo
      lockRequestType: $lockRequestType
    )
  }
`)

export const lockWipLockFormQuery = getGraphQLString(gql`
  mutation lockWipLockForm {
    lockWipLockForm
  }
`)

// TODO: remove applicationId from query/mutation
export const triggerLockDeskFailureAlert = getGraphQLString(gql`
  mutation sendLockDeskFailureSlackAlert($applicationId: Int) {
    sendLockDeskFailureSlackAlert(applicationId: $applicationId)
  }
`)

export const rateToRateInput = (rate: Rate): RateInput => ({
  adjustments: rate.adjustments,
  adjustmentsTotal: rate.adjustmentsTotal,
  apr: rate.apr,
  basePrice: rate.basePrice,
  borrowerName: rate.borrowerName,
  cashAtClose: rate.cashAtClose,
  cashOut: rate.cashOut,
  closingCosts: rate.closingCosts,
  closingFeeProvider: rate.closingFeeProvider,
  combinedLoanToValue: rate.combinedLoanToValue,
  discountOrCreditAmount: rate.discountOrCreditAmount,
  discountPoints: rate.discountPoints,
  discountPointsAsDollarAmount: rate.discountPointsAsDollarAmount,
  escrowFees: rate.escrowFees,
  estimatedPrepaidItems: rate.estimatedPrepaidItems,
  isRollInEscrow: rate.isRollInEscrow,
  family: rate.family,
  fees: rate.fees,
  finalPrice: rate.finalPrice,
  fundingFee: rate.fundingFee,
  governmentRefinanceType: rate.governmentRefinanceType,
  lenderName: rate.lenderName,
  loanAmount: rate.loanAmount,
  loanEstimateSections: rate.loanEstimateSections,
  loanProgramName: rate.loanProgramName,
  loanPurpose: rate.loanPurpose,
  loanTerm: rate.loanTerm,
  loanToValue: rate.loanToValue,
  lockId: rate.lockId,
  lockPeriod: rate.lockPeriod,
  monthlyEscrowFees: rate.monthlyEscrowFees,
  monthlyInsurance: rate.monthlyInsurance,
  monthlyPayment: rate.monthlyPayment,
  monthlyPrivateMortgageInsurance: rate.monthlyPrivateMortgageInsurance,
  monthlyPropertyTax: rate.monthlyPropertyTax,
  mortgageInsurancePremium: rate.mortgageInsurancePremium,
  originationPoints: rate.originationPoints,
  points: rate.points,
  principleAndInterestPayment: rate.principleAndInterestPayment,
  privateMortgageInsuranceQuote: rate.privateMortgageInsuranceQuote,
  propertyValue: rate.propertyValue,
  productClass: rate.productClass,
  productCode: rate.productCode,
  quoteType: rate.quoteType,
  rate: rate.rate,
  rateSheetPrice: rate.rateSheetPrice,
  rollInFees: rate.rollInFees,
  silkFees: rate.silkFees,
  subordinateLienAmount: rate.subordinateLienAmount,
  term: rate.term,
  totalLenderFees: rate.totalLenderFees,
  type: rate.type,
})
